import type { CustomEmoji as ICustomEmoji } from "emoji-mart";
import { type DOMOutputSpec, Fragment, Node as ProseMirrorNode, Schema } from "prosemirror-model";
import { EditorState, Transaction } from "prosemirror-state";
import type { NodeView } from "prosemirror-view";
import uuid from "uuid";

import { type AvailableEmojiType, CustomEmojiManager, getEmojiUuidFromUrl } from "~/common/Emoji";

import { NodeExtension } from "../extension";

export default class CustomEmoji extends NodeExtension {
	accountId: string;
	constructor(options: { accountId?: string } & { [key: string]: unknown } = {}) {
		super(options);
		const accountId = options.accountId || window.INITIAL_STATE.account?.relay_id;
		if (!accountId) {
			throw new Error("Initializing CustomEmoji extension without accountId is not allowed");
		}
		this.accountId = accountId;
	}

	emojiNameToDOMMap: { [key: string]: { nodes: Node[]; notExist?: boolean } } = {};

	get name() {
		return "custom_emoji";
	}

	init() {
		CustomEmojiManager.getInstance().onEmojiDeleted(this.onEmojiDelete);
		CustomEmojiManager.getInstance().onEmojiAdded(this.onEmojiAdd);
	}

	destroy() {
		CustomEmojiManager.getInstance().removeListener("emoji_deleted", this.onEmojiDelete);
		CustomEmojiManager.getInstance().removeListener("emoji_added", this.onEmojiAdd);
	}

	onEmojiDelete = (emoji: AvailableEmojiType) => {
		this.onEmojiUpdate(emoji, "delete");
	};

	onEmojiAdd = (emoji: AvailableEmojiType) => {
		this.onEmojiUpdate(emoji, "add");
	};

	onEmojiUpdate = (emoji: AvailableEmojiType, type: "add" | "delete") => {
		if (emoji.aliasOf) return;
		// skip if the emoji is an alias cause it won't affect how we render them
		const uuid = getEmojiUuidFromUrl(emoji.url);
		if (!uuid) return;
		const emojiInMap = this.emojiNameToDOMMap[uuid];
		if (!emojiInMap) return; // skip if the emoji is not used in the current editor view
		this.emojiNameToDOMMap[uuid].notExist = type === "delete";
		this.updateEmojiView(uuid);
	};

	// Mark the related emoji node and ask prose to recreate them
	updateEmojiView = (key: string) => {
		const { editorView } = this;
		if (!editorView) return;
		const nodes = this.emojiNameToDOMMap[key].nodes;
		if (!nodes) return;
		for (const dom of nodes) {
			const viewDesc = dom.pmViewDesc;
			if (!viewDesc) continue;
			editorView.dom.pmViewDesc?.markDirty(viewDesc.posAtStart, viewDesc.posAtEnd);
		}
		editorView.dispatch(editorView.state.tr.setMeta("updateEmoji", true));
	};

	// Keep track of the emojis' dom node
	addDomToEmojiMap = (node: ProseMirrorNode, dom: Node) => {
		const emojiInMap = this.emojiNameToDOMMap[node.attrs.uuid];
		if (!emojiInMap) {
			this.emojiNameToDOMMap[node.attrs.uuid] = {
				nodes: [dom],
			};
		} else if (!emojiInMap.nodes.includes(dom)) {
			this.emojiNameToDOMMap[node.attrs.uuid].nodes.push(dom);
		}
	};

	view = (node: ProseMirrorNode): NodeView => {
		let dom: HTMLElement;
		const that = this;
		const imgNotExist = this.emojiNameToDOMMap[node.attrs.uuid]?.notExist;
		if (this.emojiNameToDOMMap[node.attrs.uuid]?.notExist === true) {
			// if we know this emoji is not exist (most likely deleted), show its name in a span
			// There're two cases we can be sure about that the emoji is not exist:
			//   1. the emoji's image failed to load during the first time we try to render it in an img tag
			//   2. the client receives an 'EMOJI_DELETED' event through websocket
			// unless we run into one of these two cases, the 'nonExist' field remains 'undefined'
			dom = document.createElement("span");
			this.addDomToEmojiMap(node, dom);
		} else {
			// if we have no idea whether the emoji is exist or not, try to load it in an img first
			const img = new Image();
			img.onerror = () => {
				// if the image won't load, we mark the emoji as not exist and ask prose to recreate this node
				that.emojiNameToDOMMap[node.attrs.uuid].notExist = true;
				that.updateEmojiView(node.attrs.uuid);
			};
			dom = img;
			this.addDomToEmojiMap(node, dom);
			img.src = `${window.EMOJI_BASE_URL}/${node.attrs.uuid}?timestamp=${Math.floor(Date.now() / 60_000) * 60_000}`;
		}
		dom.setAttribute("data-id", node.attrs.id);
		dom.setAttribute("data-entity-id", node.attrs.entityId);
		dom.classList.add("prose-custom-emoji");
		dom.innerText = node.attrs.id;

		return {
			dom,
			update: newNode => {
				if (newNode !== node) {
					return false;
				}
				if (imgNotExist !== this.emojiNameToDOMMap[node.attrs.uuid]?.notExist) {
					// recreate the node if the existance changed
					return false;
				}
				return true;
			},
			destroy: () => {
				const domIndex = this.emojiNameToDOMMap[node.attrs.uuid].nodes.findIndex(i => i === dom);
				if (domIndex !== -1) {
					this.emojiNameToDOMMap[node.attrs.uuid].nodes.splice(domIndex, 1);
				}
			},
		};
	};
	get node() {
		return {
			group: "inline",
			content: "text{0,1}",
			inline: true,
			atom: true,
			isolating: true,
			marks: "",

			attrs: {
				id: { default: "" },
				entityId: { default: "" },
				uuid: { default: "" },
			},

			selectable: false,
			draggable: false,

			toDOM(node: ProseMirrorNode): DOMOutputSpec {
				return [
					"span",
					{
						"data-id": node.attrs.id,
						"data-uuid": node.attrs.uuid,
						"data-entity-id": uuid.v4(),
						alt: node.attrs.id,
						class: "prose-custom-emoji",
					},
					0,
				];
			},
			parseDOM: [
				// When copying from Slack's input box, the emoji is serialized as text by Slack,
				// when copying from sent message, it is serialized as emoji
				{
					// Handle copying from Slack when the emoji is in a paragraph
					tag: "span.c-emoji",
					getAttrs: (dom: HTMLElement | string) => {
						if (!(dom instanceof Element)) return false;
						const imgDOM = dom.querySelector('img[data-stringify-type="emoji"]');
						if (!imgDOM) return false;
						const emojiId = imgDOM.getAttribute("data-stringify-emoji");
						if (!emojiId) return false;
						const emojiUrl = CustomEmojiManager.getInstance().getSrc(emojiId, this.accountId);
						if (!emojiUrl) return false;
						return {
							id: emojiId,
							entityId: uuid.v4(),
							uuid: getEmojiUuidFromUrl(emojiUrl),
						};
					},
					getContent: (dom: Node, schema: Schema) => {
						if (!(dom instanceof Element)) throw new Error("invalid node");
						const imgDOM = dom.querySelector('img[data-stringify-type="emoji"]');
						if (!imgDOM) throw new Error("invalid node, no img");
						const emojiId = imgDOM.getAttribute("data-stringify-emoji");
						if (!emojiId) throw new Error("invalid node, no emojiid");
						return Fragment.from(schema.text(emojiId));
					},
				},
				{
					// Handle copying from Slack when the emoji is stand alone in the message
					tag: 'img.c-emoji[data-stringify-type="emoji"]',
					getAttrs: (dom: HTMLElement | string) => {
						if (!(dom instanceof Element)) return false;
						const emojiId = dom.getAttribute("data-stringify-emoji");
						if (!emojiId) return false;
						const emojiUrl = CustomEmojiManager.getInstance().getSrc(emojiId, this.accountId);
						if (!emojiUrl) return false;
						return {
							id: emojiId,
							entityId: uuid.v4(),
							uuid: getEmojiUuidFromUrl(emojiUrl),
						};
					},
					getContent: (dom: Node, schema: Schema) => {
						if (!(dom instanceof Element)) throw new Error("invalid node");
						const emojiId = dom.getAttribute("data-stringify-emoji");
						if (!emojiId) throw new Error("invalid node, no emojiid");
						return Fragment.from(schema.text(emojiId));
					},
				},
				{
					tag: "span.prose-custom-emoji[data-id]",
					getAttrs: (dom: HTMLElement | string) => {
						if (!(dom instanceof Element)) return false;
						const emojiId = dom.getAttribute("data-id");
						if (!emojiId) return false;
						const emojiURL = CustomEmojiManager.getInstance().getSrc(emojiId, this.accountId);
						if (!emojiURL) return false;
						if (getEmojiUuidFromUrl(emojiURL) !== dom.getAttribute("data-uuid")) return false;
						return {
							id: emojiId,
							entityId: uuid.v4(),
							uuid: dom.getAttribute("data-uuid"),
						};
					},
					contentElement: (dom: Node) => {
						const clone = dom.cloneNode(true) as HTMLElement;
						clone.innerText = clone.getAttribute("data-id") as string;
						return clone;
					},
				},
			],
		};
	}
	keys() {
		return {};
	}
	static insertEmoji =
		(emoji: ICustomEmoji, from: number, to: number) => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
			const { colons, name } = emoji;
			const entityId = uuid.v4();
			const id = colons || `:${name}:`;
			const uid = getEmojiUuidFromUrl(emoji.imageUrl);
			const node: ProseMirrorNode = state.schema.nodes["custom_emoji"].create(
				{ id, entityId, uuid: uid },
				state.schema.text(id),
			);
			if (dispatch) {
				let tr = state.tr;
				tr.replaceRangeWith(from, to, node);
				dispatch(tr);
			}
			return true;
		};
}
